// Detect whether a keydown event would mutate a keyword token (e.g., "AMAP",
// "ALAP") that the cursor is currently inside or selecting. If so, wipe the
// whole token to empty and place the cursor at the token's start. Returns
// { wiped: true, newValue, newCursor } when a wipe should happen, else
// { wiped: false }.
//
// Token boundary rules:
//   - Caret strictly between first and last keyword char  -> any mutating key wipes
//   - Caret immediately AFTER keyword (right edge)        -> Backspace wipes
//   - Caret immediately BEFORE keyword (left edge)        -> Delete wipes
//   - Selection overlapping any part of a keyword token   -> any mutating key wipes
//
// Mutating keys: single printable character (no Ctrl/Meta), Backspace, Delete.

const isMutatingKey = (event) => {
  if (event.ctrlKey || event.metaKey) return false;
  if (event.key === 'Backspace' || event.key === 'Delete') return true;
  return typeof event.key === 'string' && event.key.length === 1;
};

const findKeywordTokens = (value, keywords) => {
  const tokens = [];
  let cursor = 0;
  value.split(',').forEach((segment) => {
    const segStart = cursor;
    const segEnd = cursor + segment.length;
    const trimmed = segment.trim();
    if (trimmed && keywords.includes(trimmed.toUpperCase())) {
      const innerStart = segStart + segment.indexOf(trimmed);
      const innerEnd = innerStart + trimmed.length;
      tokens.push({ start: innerStart, end: innerEnd });
    }
    cursor = segEnd + 1; // +1 for the comma we split on
  });
  return tokens;
};

const wipeRepKeywordOnMutation = (event, value, selectionStart, selectionEnd, keywords) => {
  if (!isMutatingKey(event)) return { wiped: false };
  if (typeof value !== 'string' || value.length === 0) return { wiped: false };

  const tokens = findKeywordTokens(value, keywords);
  if (tokens.length === 0) return { wiped: false };

  const hasSelection = selectionStart !== selectionEnd;
  const matched = tokens.find((tok) => {
    if (hasSelection) {
      // Selection overlaps the token's span at all
      return selectionStart < tok.end && selectionEnd > tok.start;
    }
    if (event.key === 'Backspace') {
      // Caret strictly inside, or at the right edge (immediately after)
      return selectionStart > tok.start && selectionStart <= tok.end;
    }
    if (event.key === 'Delete') {
      // Caret strictly inside, or at the left edge (immediately before)
      return selectionStart >= tok.start && selectionStart < tok.end;
    }
    // Typing a printable char: only strict interior counts as "in the middle"
    return selectionStart > tok.start && selectionStart < tok.end;
  });

  if (!matched) return { wiped: false };

  const newValue = value.slice(0, matched.start) + value.slice(matched.end);
  return { wiped: true, newValue, newCursor: matched.start };
};

export default wipeRepKeywordOnMutation;
